import { Box, Flex } from '@chakra-ui/react'
import { css } from '@emotion/react'
import { cloneDeep, isEqual, omit } from 'lodash'
import React, { useState } from 'react'
import IconClockWait from 'public/assets/img/icon-clock-wait.svg'

import { t, Trans } from '@lingui/macro'

import {
  defaultBusinessHoursDays,
  FromToDayTimes,
  IBusinessHoursByDays,
  weekDays
} from '../../../../shared/businessHours'
import { ButtonPrimary } from 'front-end/src/components'
import { Checkbox } from '../../components/common/form/checkbox/Checkbox'
import Spinner from '../../components/common/spinner/Spinner'
import {
  useOrganisationBusinessHoursQuery,
  useOrganisationBusinessHoursPatchMutation
} from './AccountCampaignsTab.codegen'
import { AccountTabSection } from './TabContentSection'
import omitDeep from 'omit-deep-lodash'
import TimePicker from '../../components/common/date-time-pickers/TimePicker'
import SVG from 'react-inlinesvg'
import { brandColors } from 'shared/basicPalette'

export const SingleDayBusinessHoursInput: React.FC<{
  value: FromToDayTimes
  day: string
  disabled: boolean
  onChange: (val: FromToDayTimes) => void
}> = ({ value, onChange, day, disabled }) => {
  const WEEKDAYS_TRANSLATIONS = {
    sunday: t`Sunday`,
    monday: t`Monday`,
    tuesday: t`Tuesday`,
    wednesday: t`Wednesday`,
    thursday: t`Thursday`,
    friday: t`Friday`,
    saturday: t`Saturday`
  }

  if (!value) {
    // @ts-expect-error
    value = []
  }

  return (
    <Flex alignItems="baseline">
      <Box minW={100}>{WEEKDAYS_TRANSLATIONS[day]}</Box>
      <Trans>From</Trans>
      <TimePicker
        placeholder={' '}
        m="8px"
        isDisabled={disabled}
        isClearable={!disabled}
        value={value[0] as string}
        onChangeHandler={(val: string | null) => {
          if (val === '') {
            value = [null, null]
          }
          value[0] = val
          onChange(value)
        }}
      />
      <Trans>To</Trans>
      <TimePicker
        m="8px"
        isDisabled={disabled}
        placeholder={' '}
        isClearable={!disabled}
        value={value[1] as string}
        onChangeHandler={(val: string | null) => {
          if (val === '') {
            value = [null, null]
          }
          value[1] = val
          onChange(value)
        }}
      />
    </Flex>
  )
}

interface IBusinessHoursForm {
  enabled: boolean
  days?: IBusinessHoursByDays
}

export const AccountCampaignsTabForm: React.FC<IBusinessHoursForm> = (
  businessHours
) => {
  const cleanedUpData = omitDeep(businessHours, '__typename')
  const [formState, setFormState] = useState<IBusinessHoursForm>(
    // @ts-expect-error
    cleanedUpData
  )

  const defaultSchedule = React.useMemo(defaultBusinessHoursDays, [])

  const [patch, { loading }] = useOrganisationBusinessHoursPatchMutation()

  const isSaveDisabled =
    formState.days &&
    Object.values(formState.days).find((hours) => {
      return (
        hours &&
        hours.filter((h) => {
          return h
        }).length == 1
      )
    })

  return (
    <AccountTabSection>
      <Box padding="24px 0">
        <form
          onSubmit={async (e) => {
            e.preventDefault()

            const input = omit(formState, 'id')
            if (input.days) {
              for (const weekday of weekDays) {
                if (input.days[weekday] && input.days[weekday].includes(null)) {
                  delete input.days![weekday]
                }
              }
            }

            await patch({
              variables: {
                input
              }
            })
          }}
        >
          <Flex alignItems="center">
            <SVG
              src={IconClockWait}
              color={brandColors.grey[500]}
              css={{
                width: '42px',
                height: '42px',
                padding: '10px'
              }}
            />
            <h4>
              <Trans>Business hours</Trans>
            </h4>
          </Flex>
          <p>
            <Trans>
              When scheduling in campaigns, we give you the ability to restrict
              the times someone is contacted so even if they are most active at
              11pm, you have a way of restricting the sending of your campaign
              actions to a time window you wish to set as a rule for the
              business.
            </Trans>
          </p>
          <Flex margin="10px 0">
            <Checkbox
              onChange={(e) =>
                setFormState({
                  ...formState,
                  enabled: e.target.checked
                })
              }
              isChecked={formState.enabled}
            >
              <Trans>Enable business hours</Trans>
            </Checkbox>
          </Flex>
          {weekDays.map((key) => {
            return (
              <SingleDayBusinessHoursInput
                key={key}
                disabled={!formState.enabled}
                value={
                  formState?.enabled
                    ? formState.days && formState.days[key]
                    : defaultSchedule[key]
                }
                onChange={(val) => {
                  if (val) {
                    setFormState({
                      ...formState,
                      // @ts-expect-error
                      days: {
                        ...formState.days,
                        [key]: val.map((hours) => {
                          if (!hours) {
                            return null
                          }
                          return hours
                        })
                      }
                    })
                  }
                }}
                day={key}
              />
            )
          })}
          <ButtonPrimary
            type="submit"
            disabled={
              loading || isSaveDisabled || isEqual(cleanedUpData, formState)
            }
            css={css`
              float: right;
            `}
          >
            <Trans>Save</Trans>
          </ButtonPrimary>
        </form>
      </Box>
    </AccountTabSection>
  )
}

export const AccountCampaignsTab: React.FC = () => {
  const { data, loading: businessHoursLoading } =
    useOrganisationBusinessHoursQuery()

  const businessHours = cloneDeep(data?.admin?.organisation?.businessHours)

  if (businessHoursLoading || !businessHours) {
    return <Spinner />
  }
  return <AccountCampaignsTabForm {...businessHours} />
}
